import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VipRestriction from '../components/VipRestriction';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Space,
  Tag,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Statistic,
  Steps,
  Timeline,
  Tabs,
  List,
  Avatar,
  Divider,
  Progress,
  Empty
} from 'antd';
import {
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  HomeOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  EyeOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  MessageOutlined,
  CalendarOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined as CalendarIcon,
  FileTextOutlined as FileIcon,
  HeartOutlined as HeartIcon,
  MessageOutlined as MessageIcon,
  CheckCircleOutlined as DecisionIcon
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { decisionsConfig } from '../config/decisions';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;
const { TabPane } = Tabs;

const Decisions = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [decisions, setDecisions] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [decisionForm] = Form.useForm();
  const [typeForm] = Form.useForm();

  // 获取决策列表
  const fetchDecisions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(decisionsConfig.apiEndpoints.decisions);
      if (response.data.success) {
        setDecisions(response.data.decisions || []);
      } else {
        message.error(response.data.message || '获取决策列表失败');
      }
    } catch (error) {
      console.error('获取决策列表失败:', error);
      message.error('获取决策列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取决策类型
  const fetchTypes = async () => {
    try {
      const response = await axios.get(decisionsConfig.apiEndpoints.types);
      if (response.data.success) {
        setTypes(response.data.types || []);
      }
    } catch (error) {
      console.error('获取决策类型失败:', error);
    }
  };

  // 打开决策弹窗
  const handleOpenDecision = (decision = null) => {
    setSelectedDecision(decision);
    if (decision) {
      decisionForm.setFieldsValue({
        ...decision,
        created_date: decision.created_date ? dayjs(decision.created_date) : null,
        target_date: decision.target_date ? dayjs(decision.target_date) : null
      });
    } else {
      decisionForm.resetFields();
    }
    setShowDecisionModal(true);
  };

  // 打开类型弹窗
  const handleOpenType = (type = null) => {
    setSelectedType(type);
    if (type) {
      typeForm.setFieldsValue(type);
    } else {
      typeForm.resetFields();
    }
    setShowTypeModal(true);
  };

  // 保存决策
  const handleSaveDecision = async (values) => {
    try {
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        ...values,
        created_date: values.created_date ? (dayjs.isDayjs(values.created_date) ? values.created_date.format('YYYY-MM-DD') : values.created_date) : null,
        target_date: values.target_date ? (dayjs.isDayjs(values.target_date) ? values.target_date.format('YYYY-MM-DD') : values.target_date) : null
      };

      const url = selectedDecision 
        ? `${decisionsConfig.apiEndpoints.updateDecision}/${selectedDecision.id}`
        : decisionsConfig.apiEndpoints.createDecision;
      
      const method = selectedDecision ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');

      let response;
      if (selectedDecision) {
        response = await axios.put(`${decisionsConfig.apiEndpoints.updateDecision}/${selectedDecision.id}`, requestBody);
      } else {
        response = await axios.post(decisionsConfig.apiEndpoints.createDecision, requestBody);
      }

      if (response.data.success) {
        message.success(selectedDecision ? '更新成功' : '创建成功');
        setShowDecisionModal(false);
        fetchDecisions();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存决策失败:', error);
      message.error('操作失败');
    }
  };

  // 保存类型
  const handleSaveType = async (values) => {
    try {
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        ...values
      };

      const url = selectedType 
        ? `${decisionsConfig.apiEndpoints.updateType}/${selectedType.id}`
        : decisionsConfig.apiEndpoints.createType;
      
      const method = selectedType ? 'PUT' : 'POST';
      const token = localStorage.getItem('token') || '';

      let response;
      if (selectedType) {
        response = await axios.put(`${decisionsConfig.apiEndpoints.updateType}/${selectedType.id}`, requestBody);
      } else {
        response = await axios.post(decisionsConfig.apiEndpoints.createType, requestBody);
      }

      if (response.data.success) {
        message.success(selectedType ? '更新成功' : '创建成功');
        setShowTypeModal(false);
        fetchTypes();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存类型失败:', error);
      message.error('操作失败');
    }
  };

  // 删除决策
  const handleDeleteDecision = async (id) => {
    try {
      const response = await axios.delete(`${decisionsConfig.apiEndpoints.deleteDecision}/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchDecisions();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除决策失败:', error);
      message.error('删除失败');
    }
  };

  // 删除类型
  const handleDeleteType = async (id) => {
    try {
      const response = await axios.delete(`${decisionsConfig.apiEndpoints.deleteType}/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchTypes();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除类型失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = decisionsConfig.decisionTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = decisionsConfig.decisionTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = decisionsConfig.decisionTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    const statusObj = decisionsConfig.decisionStatus.find(s => s.key === status);
    return statusObj ? statusObj.color : 'default';
  };

  // 获取状态名称
  const getStatusName = (status) => {
    const statusObj = decisionsConfig.decisionStatus.find(s => s.key === status);
    return statusObj ? statusObj.name : '未知';
  };

  // 获取优先级颜色
  const getPriorityColor = (priority) => {
    const priorityObj = decisionsConfig.priorities.find(p => p.key === priority);
    return priorityObj ? priorityObj.color : 'default';
  };

  // 获取优先级名称
  const getPriorityName = (priority) => {
    const priorityObj = decisionsConfig.priorities.find(p => p.key === priority);
    return priorityObj ? priorityObj.name : '未知';
  };

  // 计算统计信息
  const totalDecisions = decisions.length;
  const completedDecisions = decisions.filter(decision => decision.status === 'completed').length;
  const pendingDecisions = decisions.filter(decision => decision.status === 'pending').length;
  const completionRate = totalDecisions > 0 ? Math.round((completedDecisions / totalDecisions) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchDecisions();
    fetchTypes();
  }, [initialize]);

  return (
    <VipRestriction>
      <div className="mod-page">
      {/* 头部导航 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <CheckCircleOutlined style={{ marginRight: '8px', color: decisionsConfig.bgColor }} />
            {decisionsConfig.name}
          </div>
          <div className="mod-header-subtitle">
            记录、分析、执行、复盘 —— 让每一个重要决策有据可依
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenDecision()}
            style={{ backgroundColor: decisionsConfig.bgColor, borderColor: decisionsConfig.bgColor }}
          >
            新增决策
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => handleOpenType()}
            style={{ borderColor: decisionsConfig.bgColor, color: decisionsConfig.bgColor }}
          >
            决策类型
          </Button>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setShowTipsModal(true)}
            style={{ borderColor: decisionsConfig.bgColor, color: decisionsConfig.bgColor }}
          >
            决策TIPS
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(99,102,241,0.12)', color: '#6366F1' }}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{totalDecisions}</div>
            <div className="mod-stat-label">决策总数</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(82,196,26,0.12)', color: '#52C41A' }}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{completedDecisions}</div>
            <div className="mod-stat-label">已完成</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(250,173,20,0.14)', color: '#FA8C16' }}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{pendingDecisions}</div>
            <div className="mod-stat-label">待决策</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(24,144,255,0.12)', color: '#1890FF' }}>
            <BarChartOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{completionRate}%</div>
            <div className="mod-stat-label">完成率</div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div>

        <Tabs defaultActiveKey="decisions">

          <Row gutter={[16, 16]} style={{ margin: 0 }}>
            {decisions.map((decision) => (
              <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3} key={decision.id}>
                <Card
                  hoverable
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  styles={{ body: { padding: '6px' } }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                  actions={[
                    <EditOutlined key="edit" onClick={() => handleOpenDecision(decision)} />,
                    <Popconfirm
                      title="确定删除这个决策吗？"
                      onConfirm={() => handleDeleteDecision(decision.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <DeleteOutlined key="delete" />
                    </Popconfirm>
                  ]}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                      {getTypeIcon(decision.decision_type)}
                    </div>
                    <Title level={5} style={{ margin: '8px 0' }}>
                      {decision.name}
                    </Title>
                    <Tag color={getTypeColor(decision.decision_type)} style={{ marginBottom: '8px' }}>
                      {getTypeName(decision.decision_type)}
                    </Tag>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📅 {decision.created_date ? dayjs(decision.created_date).format('MM-DD') : '未设置'}
                      </Text>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Tag color={getStatusColor(decision.status)}>
                        {getStatusName(decision.status)}
                      </Tag>
                    </div>
                    {decision.priority && (
                      <div style={{ marginTop: '4px' }}>
                        <Tag color={getPriorityColor(decision.priority)}>
                          {getPriorityName(decision.priority)}优先级
                        </Tag>
                      </div>
                    )}
                    {decision.objective && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                        🎯 {decision.objective}
                      </Text>
                    )}
                    {decision.implementation_progress && (
                      <div style={{ marginTop: '8px' }}>
                        <Progress 
                          percent={parseInt(decision.implementation_progress)} 
                          size="small"
                          showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          进度: {decision.implementation_progress}%
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
            {decisions.length === 0 && (
              <Col span={24}>
                <Card className="mod-card" styles={{ body: { padding: '48px 24px' } }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<Text type="secondary">暂无决策，点击“新增决策”开始管理你的决策</Text>}
                  >
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleOpenDecision()}
                      style={{ backgroundColor: decisionsConfig.bgColor, borderColor: decisionsConfig.bgColor }}
                    >
                      新增决策
                    </Button>
                  </Empty>
                </Card>
              </Col>
            )}
          </Row>
        </Tabs>

      {/* 决策弹窗 */}
      {showDecisionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                {selectedDecision ? '编辑' : '新增'}决策
              </h3>
              <Button 
                type="text" 
                onClick={() => setShowDecisionModal(false)}
                style={{ fontSize: '18px', width: '24px', height: '24px' }}
              >
                ×
              </Button>
            </div>

            <Form
              form={decisionForm}
              layout="vertical"
              onFinish={handleSaveDecision}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="decision_type"
                    label="决策类型"
                    rules={[{ required: true, message: '请选择决策类型' }]}
                  >
                    <select 
                      placeholder="请选择类型"
                      value={decisionForm.getFieldValue('decision_type') || ''}
                      onChange={(e) => decisionForm.setFieldValue('decision_type', e.target.value)}
                      style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                    >
                      <option value="">请选择类型</option>
                      {decisionsConfig.decisionTypes.map(type => (
                        <option key={type.key} value={type.key}>
                          {type.icon} {type.name}
                        </option>
                      ))}
                    </select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="status"
                    label="决策状态"
                    rules={[{ required: true, message: '请选择决策状态' }]}
                  >
                    <select 
                      placeholder="请选择状态"
                      value={decisionForm.getFieldValue('status') || ''}
                      onChange={(e) => decisionForm.setFieldValue('status', e.target.value)}
                      style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                    >
                      <option value="">请选择状态</option>
                      {decisionsConfig.decisionStatus.map(status => (
                        <option key={status.key} value={status.key}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="name"
                label="决策名称"
                rules={[{ required: true, message: '请输入决策名称' }]}
              >
                <Input placeholder="请输入决策名称" />
              </Form.Item>
              
              <Form.Item
                name="objective"
                label="决策目标"
                rules={[{ required: true, message: '请输入决策目标' }]}
              >
                <TextArea rows={2} placeholder="请描述决策要达到的目标" />
              </Form.Item>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="created_date"
                    label="创建日期"
                  >
                    <Input placeholder="决策创建日期" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="target_date"
                    label="目标日期"
                  >
                    <Input placeholder="预期完成日期" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="priority"
                label="优先级"
              >
                <select 
                  placeholder="请选择优先级"
                  value={decisionForm.getFieldValue('priority') || ''}
                  onChange={(e) => decisionForm.setFieldValue('priority', e.target.value)}
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                >
                  <option value="">请选择优先级</option>
                  {decisionsConfig.priorities.map(priority => (
                    <option key={priority.key} value={priority.key}>
                      {priority.name}
                    </option>
                  ))}
                </select>
              </Form.Item>
              
              <Form.Item
                name="reference_info"
                label="参考信息"
              >
                <TextArea rows={3} placeholder="请记录决策相关的参考信息和数据" />
              </Form.Item>
              
              <Form.Item
                name="solution_analysis"
                label="方案分析"
              >
                <TextArea rows={3} placeholder="请记录各方案的优缺点分析" />
              </Form.Item>
              
              <Form.Item
                name="final_solution"
                label="最终方案"
              >
                <TextArea rows={2} placeholder="请记录最终选择的方案" />
              </Form.Item>
              
              <Form.Item
                name="implementation_progress"
                label="实施进度"
              >
                <Input placeholder="请输入实施进度百分比（0-100）" />
              </Form.Item>
              
              <Form.Item
                name="monitoring_followup"
                label="监控跟进"
              >
                <TextArea rows={2} placeholder="请记录监控和跟进情况" />
              </Form.Item>
              
              <Form.Item
                name="review_improvement"
                label="复盘改进"
              >
                <TextArea rows={2} placeholder="请记录复盘总结和改进建议" />
              </Form.Item>
              
              <Form.Item
                name="notes"
                label="备注"
              >
                <TextArea rows={2} placeholder="请输入备注信息" />
              </Form.Item>
            </Form>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowDecisionModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => decisionForm.submit()}>
                {selectedDecision ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 类型管理弹窗 */}
      {showTypeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                {selectedType ? '编辑' : '新增'}决策类型
              </h3>
              <Button 
                type="text" 
                onClick={() => setShowTypeModal(false)}
                style={{ fontSize: '18px', width: '24px', height: '24px' }}
              >
                ×
              </Button>
            </div>

            <Form
              form={typeForm}
              layout="vertical"
              onFinish={handleSaveType}
            >
              <Form.Item
                name="name"
                label="类型名称"
                rules={[{ required: true, message: '请输入类型名称' }]}
              >
                <Input placeholder="请输入类型名称" />
              </Form.Item>
              
              <Form.Item
                name="description"
                label="类型描述"
              >
                <TextArea rows={3} placeholder="请输入类型描述" />
              </Form.Item>
              
              <Form.Item
                name="color"
                label="显示颜色"
              >
                <Input placeholder="请输入颜色代码，如：#1890FF" />
              </Form.Item>
            </Form>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowTypeModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => typeForm.submit()}>
                {selectedType ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 决策TIPS弹窗 */}
      {showTipsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '900px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                <DecisionIcon style={{ color: decisionsConfig.bgColor, marginRight: '8px' }} />
                决策管理TIPS
              </h3>
              <Button 
                type="text" 
                onClick={() => setShowTipsModal(false)}
                style={{ fontSize: '18px', width: '24px', height: '24px' }}
              >
                ×
              </Button>
            </div>

            <div style={{ padding: '16px 0' }}>
              {/* 决策管理步骤 */}
              <Card title="决策管理核心步骤" style={{ marginBottom: '16px' }}>
                <Steps direction="vertical" size="small">
                  {decisionsConfig.decisionManagementTips.steps.map((step, index) => (
                    <Step
                      key={index}
                      title={
                        <div>
                          <span style={{ marginRight: '8px' }}>{step.icon}</span>
                          {step.title}
                        </div>
                      }
                      description={
                        <div>
                          <Text strong>{step.description}</Text>
                          <br />
                          <Text type="secondary">{step.details}</Text>
                        </div>
                      }
                    />
                  ))}
                </Steps>
              </Card>

              {/* 决策分析方法 */}
              <Card title="常用决策分析方法" style={{ marginBottom: '16px' }}>
                <Row gutter={[16, 16]}>
                  {decisionsConfig.decisionAnalysisMethods.methods.map((method, index) => (
                    <Col xs={24} sm={12} key={index}>
                      <Card size="small" hoverable>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                            {method.icon}
                          </div>
                          <Title level={5} style={{ margin: '8px 0' }}>
                            {method.name}
                          </Title>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                            {method.description}
                          </Text>
                          <List
                            size="small"
                            dataSource={method.steps}
                            renderItem={(step) => (
                              <List.Item style={{ padding: '4px 0' }}>
                                <Text style={{ fontSize: '12px' }}>• {step}</Text>
                              </List.Item>
                            )}
                          />
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>

              {/* 决策管理原则 */}
              <Card title="决策管理核心原则" style={{ marginBottom: '16px' }}>
                <Timeline>
                  {decisionsConfig.decisionManagementPrinciples.principles.map((principle, index) => (
                    <Timeline.Item key={index}>
                      <Title level={5}>
                        <span style={{ marginRight: '8px' }}>{principle.icon}</span>
                        {principle.title}
                      </Title>
                      <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                        {principle.description}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>

              {/* 应用场景 */}
              <Card title="决策管理应用场景">
                <Row gutter={[16, 16]}>
                  {decisionsConfig.decisionManagementScenarios.scenarios.map((scenario, index) => (
                    <Col xs={24} sm={8} key={index}>
                      <Card size="small" hoverable>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                            {scenario.icon}
                          </div>
                          <Title level={5} style={{ margin: '8px 0' }}>
                            {scenario.title}
                          </Title>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                            {scenario.description}
                          </Text>
                          <List
                            size="small"
                            dataSource={scenario.examples}
                            renderItem={(example) => (
                              <List.Item style={{ padding: '4px 0' }}>
                                <Text style={{ fontSize: '12px' }}>• {example}</Text>
                              </List.Item>
                            )}
                          />
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowTipsModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </VipRestriction>
  );
};

export default Decisions;
